import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

type CacheStore = Record<string, unknown>;

interface PageCacheContextValue {
  getValue: <T>(key: string) => T | undefined;
  setValue: (key: string, value: unknown) => void;
  clearValue: (key: string) => void;
  clearAll: () => void;
}

const PageCacheContext = createContext<PageCacheContextValue | undefined>(
  undefined
);

export const PageCacheProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const cacheRef = useRef<CacheStore>({});

  const getValue = useCallback(<T,>(key: string): T | undefined => {
    return cacheRef.current[key] as T | undefined;
  }, []);

  const setValue = useCallback((key: string, value: unknown) => {
    cacheRef.current[key] = value;
  }, []);

  const clearValue = useCallback((key: string) => {
    delete cacheRef.current[key];
  }, []);

  const clearAll = useCallback(() => {
    cacheRef.current = {};
  }, []);

  return (
    <PageCacheContext.Provider
      value={{ getValue, setValue, clearValue, clearAll }}
    >
      {children}
    </PageCacheContext.Provider>
  );
};

export const usePageCacheState = <T,>(
  key: string,
  initialValue: T
): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const context = useContext(PageCacheContext);
  if (!context) {
    throw new Error('usePageCacheState must be used within PageCacheProvider');
  }

  const { getValue, setValue } = context;

  const [state, setState] = useState<T>(() => {
    const cached = getValue<T>(key);
    return cached !== undefined ? cached : initialValue;
  });

  const setCachedState = useCallback(
    (value: React.SetStateAction<T>) => {
      setState((prev) => {
        const resolved =
          typeof value === 'function' ? (value as (prev: T) => T)(prev) : value;
        setValue(key, resolved);
        return resolved;
      });
    },
    [key, setValue]
  );

  return [state, setCachedState];
};

export const usePageCacheActions = () => {
  const context = useContext(PageCacheContext);
  if (!context) {
    throw new Error('usePageCacheActions must be used within PageCacheProvider');
  }
  return context;
};

